// Development Studies Weekly Lessons/Scheme of Work - Years 11-13
// Based on Vanuatu National Curriculum

export const developmentStudiesWeeklyLessons = {
  year11: {
    term1: { weeks: [
      {
        week: 1,
        topic: "Introduction to Development Studies",
        strand: "DST1",
        learningOutcomes: ["Define development", "Understand course overview"],
        activities: ["Course introduction", "Discussion on what development means"],
        resources: ["Textbook Chapter 1", "Development diagrams"],
        assessment: "Baseline quiz"
      },
      {
        week: 2,
        topic: "Aspects of Development",
        strand: "DST1",
        learningOutcomes: ["List the six aspects of development", "Explain interrelationships"],
        activities: ["Create development star diagrams", "Group discussions"],
        resources: ["Star diagram templates", "Case study examples"],
        assessment: "Diagram assessment"
      },
      {
        week: 3,
        topic: "Economic Development",
        strand: "DST1",
        learningOutcomes: ["Define economic development", "Distinguish from other aspects"],
        activities: ["Analyze economic indicators", "Compare countries"],
        resources: ["World Bank data", "Country profiles"],
        assessment: "Written exercise"
      },
      {
        week: 4,
        topic: "Social and Political Development",
        strand: "DST1",
        learningOutcomes: ["Define social and political development", "Give examples"],
        activities: ["Case study analysis", "Class debate"],
        resources: ["News articles", "HDI reports"],
        assessment: "Group presentation"
      },
      {
        week: 5,
        topic: "Development Indicators - GDP",
        strand: "DST1",
        learningOutcomes: ["Define GDP and GNP", "Calculate GDP per capita"],
        activities: ["GDP calculation exercises", "Country comparisons"],
        resources: ["Calculator", "Economic data sheets"],
        assessment: "Calculation quiz"
      },
      {
        week: 6,
        topic: "Human Development Index",
        strand: "DST1",
        learningOutcomes: ["Explain HDI components", "Interpret HDI rankings"],
        activities: ["HDI analysis activity", "Research task"],
        resources: ["UNDP Human Development Report", "HDI tables"],
        assessment: "Research report"
      },
      {
        week: 7,
        topic: "Limitations of Development Indicators",
        strand: "DST1",
        learningOutcomes: ["Discuss limitations of GDP and HDI", "Suggest alternatives"],
        activities: ["Critical analysis exercise", "Class discussion"],
        resources: ["Alternative indicator examples", "Discussion prompts"],
        assessment: "Essay writing"
      },
      {
        week: 8,
        topic: "Millennium/Sustainable Development Goals",
        strand: "DST1",
        learningOutcomes: ["List MDGs and SDGs", "Explain their importance"],
        activities: ["SDG poster creation", "Research on Vanuatu's progress"],
        resources: ["UN SDG materials", "Vanuatu progress reports"],
        assessment: "Poster presentation"
      },
      {
        week: 9,
        topic: "Economic Systems Introduction",
        strand: "DST2",
        learningOutcomes: ["Define economic systems", "Identify types"],
        activities: ["Economic systems comparison chart", "Discussion"],
        resources: ["Economics textbook", "System diagrams"],
        assessment: "Comparison exercise"
      },
      {
        week: 10,
        topic: "Subsistence Economy",
        strand: "DST2",
        learningOutcomes: ["Define subsistence economy", "Describe features"],
        activities: ["Case study of Vanuatu village", "Field observation"],
        resources: ["Local economy examples", "Interview guides"],
        assessment: "Observation report"
      }
    ] },
    term2: { weeks: [
      {
        week: 1,
        topic: "Commercial Economy",
        strand: "DST2",
        learningOutcomes: ["Define commercial economy", "Compare with subsistence"],
        activities: ["Business case studies", "Market analysis"],
        resources: ["Business examples", "Economic reports"],
        assessment: "Comparison essay"
      },
      {
        week: 2,
        topic: "Mixed Economy in Vanuatu",
        strand: "DST2",
        learningOutcomes: ["Explain mixed economy", "Apply to Vanuatu context"],
        activities: ["Economic mapping of Vanuatu", "Discussion"],
        resources: ["Vanuatu economic data", "Maps"],
        assessment: "Class quiz"
      },
      {
        week: 3,
        topic: "Sectors of Economic Development",
        strand: "DST2",
        learningOutcomes: ["Name four economic sectors", "Give examples"],
        activities: ["Sector classification activity", "Research task"],
        resources: ["Sector diagrams", "Employment data"],
        assessment: "Classification test"
      },
      {
        week: 4,
        topic: "Primary Sector Activities",
        strand: "DST2",
        learningOutcomes: ["Identify primary sector activities", "Discuss importance"],
        activities: ["Primary sector case studies", "Field visit preparation"],
        resources: ["Agriculture/fishing data", "Photos"],
        assessment: "Case study report"
      },
      {
        week: 5,
        topic: "Agriculture Systems",
        strand: "DST2",
        learningOutcomes: ["Compare farming types", "Draw system diagrams"],
        activities: ["Agricultural system flow charts", "Comparison activity"],
        resources: ["Farm diagrams", "Templates"],
        assessment: "Diagram assessment"
      },
      {
        week: 6,
        topic: "International Aid Introduction",
        strand: "DST3",
        learningOutcomes: ["Define international aid", "Identify types"],
        activities: ["Aid type classification", "Discussion"],
        resources: ["Aid examples", "Donor information"],
        assessment: "Quiz"
      },
      {
        week: 7,
        topic: "Types and Sources of Aid",
        strand: "DST3",
        learningOutcomes: ["Distinguish bilateral and multilateral aid", "Name major donors"],
        activities: ["Aid mapping activity", "Research task"],
        resources: ["Aid flow charts", "Donor profiles"],
        assessment: "Research presentation"
      },
      {
        week: 8,
        topic: "Advantages and Disadvantages of Aid",
        strand: "DST3",
        learningOutcomes: ["Discuss pros and cons of aid", "Form opinions"],
        activities: ["Debate preparation", "Class debate"],
        resources: ["Debate materials", "Case studies"],
        assessment: "Debate participation"
      },
      {
        week: 9,
        topic: "Technology and Development",
        strand: "DST3",
        learningOutcomes: ["Identify major technological changes", "Discuss impacts"],
        activities: ["Technology timeline creation", "Discussion"],
        resources: ["History of technology materials", "Timeline templates"],
        assessment: "Timeline assessment"
      },
      {
        week: 10,
        topic: "Digital Divide",
        strand: "DST3",
        learningOutcomes: ["Explain digital divide", "Discuss implications for Vanuatu"],
        activities: ["Internet access research", "Class discussion"],
        resources: ["Internet statistics", "Digital divide articles"],
        assessment: "Written reflection"
      }
    ] },
    term3: { weeks: [
      {
        week: 1,
        topic: "The Earth in Space",
        strand: "DST4",
        learningOutcomes: ["Describe solar system", "Explain Earth's unique features"],
        activities: ["Solar system diagram", "Discussion"],
        resources: ["Space diagrams", "Videos"],
        assessment: "Diagram quiz"
      },
      {
        week: 2,
        topic: "Climate Zones and Biomes",
        strand: "DST4",
        learningOutcomes: ["Identify climate zones", "Describe major biomes"],
        activities: ["Climate zone mapping", "Biome research"],
        resources: ["World maps", "Climate data"],
        assessment: "Mapping exercise"
      },
      {
        week: 3,
        topic: "Greenhouse Effect",
        strand: "DST4",
        learningOutcomes: ["Explain greenhouse effect", "Name greenhouse gases"],
        activities: ["Greenhouse effect diagram", "Experiment"],
        resources: ["Diagrams", "Science equipment"],
        assessment: "Explanation test"
      },
      {
        week: 4,
        topic: "Climate Change Impacts",
        strand: "DST4",
        learningOutcomes: ["Describe climate change effects", "Apply to Pacific"],
        activities: ["Impact research", "Case study analysis"],
        resources: ["Climate reports", "Pacific data"],
        assessment: "Research report"
      },
      {
        week: 5,
        topic: "Natural Resources",
        strand: "DST4",
        learningOutcomes: ["Define natural resources", "Classify types"],
        activities: ["Resource classification", "Vanuatu resource mapping"],
        resources: ["Resource lists", "Maps"],
        assessment: "Classification test"
      },
      {
        week: 6,
        topic: "Renewable vs Non-Renewable",
        strand: "DST4",
        learningOutcomes: ["Distinguish resource types", "Discuss sustainability"],
        activities: ["Comparison activity", "Sustainability discussion"],
        resources: ["Examples", "Discussion prompts"],
        assessment: "Written exercise"
      },
      {
        week: 7,
        topic: "Human Societies Development",
        strand: "DST5",
        learningOutcomes: ["Trace society evolution", "Identify features"],
        activities: ["Timeline creation", "Comparison chart"],
        resources: ["History materials", "Timeline templates"],
        assessment: "Timeline assessment"
      },
      {
        week: 8,
        topic: "Traditional Vanuatu Society",
        strand: "DST5",
        learningOutcomes: ["Describe traditional features", "Discuss changes"],
        activities: ["Elder interview preparation", "Discussion"],
        resources: ["Cultural materials", "Interview guides"],
        assessment: "Interview report"
      },
      {
        week: 9,
        topic: "Population Growth",
        strand: "DST5",
        learningOutcomes: ["Explain population concepts", "Discuss effects"],
        activities: ["Population data analysis", "Calculation exercises"],
        resources: ["Population statistics", "Calculators"],
        assessment: "Data analysis test"
      },
      {
        week: 10,
        topic: "Year 11 Review and Examination",
        strand: "All",
        learningOutcomes: ["Review all topics", "Prepare for examination"],
        activities: ["Review sessions", "Practice tests"],
        resources: ["Review materials", "Past papers"],
        assessment: "End of year examination"
      }
    ] },
    term4: { weeks: [
      {
        week: 1,
        topic: "Migration Patterns",
        strand: "DST5",
        learningOutcomes: ["Describe Pacific migrations", "Explain language diversity"],
        activities: ["Migration mapping", "Language research"],
        resources: ["Migration maps", "Language data"],
        assessment: "Mapping assessment"
      },
      {
        week: 2,
        topic: "Cultural and Spiritual Development",
        strand: "DST5",
        learningOutcomes: ["Define cultural development", "Discuss importance"],
        activities: ["Cultural comparison activity", "Discussion"],
        resources: ["Cultural examples", "Discussion prompts"],
        assessment: "Reflection essay"
      },
      {
        week: 3,
        topic: "Review and Project Work",
        strand: "All",
        learningOutcomes: ["Complete project work", "Prepare presentations"],
        activities: ["Project completion", "Presentation preparation"],
        resources: ["Project guidelines", "Presentation materials"],
        assessment: "Project submission"
      },
      {
        week: 4,
        topic: "Final Presentations",
        strand: "All",
        learningOutcomes: ["Present projects", "Receive feedback"],
        activities: ["Student presentations", "Peer evaluation"],
        resources: ["Evaluation rubrics"],
        assessment: "Presentation grade"
      }
    ] }
  },
  year12: {
    term1: { weeks: [
      {
        week: 1,
        topic: "Review of Development Concepts",
        strand: "DST1",
        learningOutcomes: ["Review Year 11 concepts", "Introduce Year 12 content"],
        activities: ["Review quiz", "Discussion"],
        resources: ["Year 11 notes", "Year 12 overview"],
        assessment: "Diagnostic test"
      },
      {
        week: 2,
        topic: "Quality of Life Survey Design",
        strand: "DST1",
        learningOutcomes: ["Understand survey purpose", "Design survey questions"],
        activities: ["Survey design workshop", "Question writing"],
        resources: ["Survey templates", "Examples"],
        assessment: "Draft survey"
      },
      {
        week: 3,
        topic: "Data Collection Methods",
        strand: "DST1",
        learningOutcomes: ["Learn data collection techniques", "Plan fieldwork"],
        activities: ["Method practice", "Planning session"],
        resources: ["Interview guides", "Observation forms"],
        assessment: "Method selection"
      },
      {
        week: 4,
        topic: "Conducting the Survey",
        strand: "DST1",
        learningOutcomes: ["Collect survey data", "Record responses"],
        activities: ["Fieldwork - community survey"],
        resources: ["Survey forms", "Recording equipment"],
        assessment: "Data collection"
      },
      {
        week: 5,
        topic: "Data Analysis",
        strand: "DST1",
        learningOutcomes: ["Analyze collected data", "Create graphs"],
        activities: ["Data entry", "Graph creation"],
        resources: ["Spreadsheets", "Graph paper"],
        assessment: "Data analysis"
      },
      {
        week: 6,
        topic: "Survey Report Writing",
        strand: "DST1",
        learningOutcomes: ["Write survey report", "Present findings"],
        activities: ["Report writing", "Peer review"],
        resources: ["Report templates", "Writing guides"],
        assessment: "Survey report submission"
      },
      {
        week: 7,
        topic: "Development Gap",
        strand: "DST1",
        learningOutcomes: ["Define development gap", "Analyze causes"],
        activities: ["Gap analysis", "Discussion"],
        resources: ["Development data", "Country comparisons"],
        assessment: "Analysis exercise"
      },
      {
        week: 8,
        topic: "Colonialism and Development",
        strand: "DST1",
        learningOutcomes: ["Explain colonial impact", "Trace historical effects"],
        activities: ["Historical analysis", "Case studies"],
        resources: ["History texts", "Maps"],
        assessment: "Essay"
      },
      {
        week: 9,
        topic: "World Trade Patterns",
        strand: "DST1",
        learningOutcomes: ["Describe trade patterns", "Explain inequalities"],
        activities: ["Trade flow analysis", "Discussion"],
        resources: ["Trade data", "Flow diagrams"],
        assessment: "Analysis quiz"
      },
      {
        week: 10,
        topic: "Terms of Trade",
        strand: "DST1",
        learningOutcomes: ["Explain terms of trade", "Discuss fair trade"],
        activities: ["Fair trade research", "Class discussion"],
        resources: ["Trade examples", "Fair trade materials"],
        assessment: "Research task"
      }
    ] },
    term2: { weeks: [
      {
        week: 1,
        topic: "Economic Indicators Review",
        strand: "DST2",
        learningOutcomes: ["Review GDP, HDI calculations", "Practice applications"],
        activities: ["Calculation practice", "Country analysis"],
        resources: ["Data sheets", "Calculators"],
        assessment: "Calculation test"
      },
      {
        week: 2,
        topic: "Food Security Introduction",
        strand: "DST2",
        learningOutcomes: ["Define food security dimensions", "Analyze challenges"],
        activities: ["Food security mapping", "Discussion"],
        resources: ["FAO materials", "Statistics"],
        assessment: "Concept quiz"
      },
      {
        week: 3,
        topic: "Poverty and Hunger",
        strand: "DST2",
        learningOutcomes: ["Define poverty types", "Explain causes of hunger"],
        activities: ["Poverty case studies", "Analysis"],
        resources: ["Case study materials", "Statistics"],
        assessment: "Case study report"
      },
      {
        week: 4,
        topic: "Climate Change and Agriculture",
        strand: "DST2",
        learningOutcomes: ["Analyze climate impacts on farming", "Discuss adaptations"],
        activities: ["Impact research", "Adaptation planning"],
        resources: ["Climate data", "Agricultural reports"],
        assessment: "Research presentation"
      },
      {
        week: 5,
        topic: "Food Security Seminar",
        strand: "DST2",
        learningOutcomes: ["Present food security research", "Discuss solutions"],
        activities: ["Student seminars", "Panel discussion"],
        resources: ["Presentation materials"],
        assessment: "Seminar presentation"
      },
      {
        week: 6,
        topic: "Industrialization",
        strand: "DST2",
        learningOutcomes: ["Define industrialization", "Describe industrial revolution"],
        activities: ["Historical analysis", "Discussion"],
        resources: ["History texts", "Industrial examples"],
        assessment: "Written exercise"
      },
      {
        week: 7,
        topic: "Multinational Corporations",
        strand: "DST2",
        learningOutcomes: ["Identify MNCs", "Analyze impacts"],
        activities: ["MNC research", "Impact analysis"],
        resources: ["Company profiles", "Economic data"],
        assessment: "Research report"
      },
      {
        week: 8,
        topic: "Small Enterprises and Micro-finance",
        strand: "DST2",
        learningOutcomes: ["Explain micro-finance", "Discuss importance"],
        activities: ["Local business case study", "Discussion"],
        resources: ["Micro-finance examples", "Local business data"],
        assessment: "Case study"
      },
      {
        week: 9,
        topic: "Employment and Development",
        strand: "DST2",
        learningOutcomes: ["Analyze employment issues", "Discuss labor markets"],
        activities: ["Employment data analysis", "Discussion"],
        resources: ["Labor statistics", "Employment data"],
        assessment: "Data analysis"
      },
      {
        week: 10,
        topic: "Mid-Year Review",
        strand: "All",
        learningOutcomes: ["Review Term 1-2 content", "Prepare for assessment"],
        activities: ["Review sessions", "Practice tests"],
        resources: ["Review materials"],
        assessment: "Mid-year examination"
      }
    ] },
    term3: { weeks: [
      {
        week: 1,
        topic: "Rural Development",
        strand: "DST3",
        learningOutcomes: ["Define rural development", "Identify challenges"],
        activities: ["Rural area case study", "Discussion"],
        resources: ["Rural development materials", "Statistics"],
        assessment: "Case study analysis"
      },
      {
        week: 2,
        topic: "Community-Based Development",
        strand: "DST3",
        learningOutcomes: ["Explain bottom-up approaches", "Analyze examples"],
        activities: ["Community project analysis", "Discussion"],
        resources: ["Project examples", "NGO materials"],
        assessment: "Analysis exercise"
      },
      {
        week: 3,
        topic: "Development Organizations",
        strand: "DST3",
        learningOutcomes: ["Identify development organizations", "Explain their roles"],
        activities: ["Organization research", "Presentation"],
        resources: ["Organization profiles", "Website research"],
        assessment: "Research presentation"
      },
      {
        week: 4,
        topic: "Climate Change Review",
        strand: "DST4",
        learningOutcomes: ["Review climate change concepts", "Analyze Pacific impacts"],
        activities: ["Impact analysis", "Discussion"],
        resources: ["Climate reports", "Pacific data"],
        assessment: "Analysis quiz"
      },
      {
        week: 5,
        topic: "Climate Adaptation Strategies",
        strand: "DST4",
        learningOutcomes: ["Identify adaptation strategies", "Apply to Vanuatu"],
        activities: ["Strategy research", "Planning exercise"],
        resources: ["Adaptation examples", "Planning templates"],
        assessment: "Strategy plan"
      },
      {
        week: 6,
        topic: "Health and Development",
        strand: "DST5",
        learningOutcomes: ["Explain health-development link", "Analyze health indicators"],
        activities: ["Health data analysis", "Discussion"],
        resources: ["Health statistics", "WHO data"],
        assessment: "Data analysis"
      },
      {
        week: 7,
        topic: "Disease and Development",
        strand: "DST5",
        learningOutcomes: ["Identify major diseases", "Discuss prevention"],
        activities: ["Disease research", "Prevention planning"],
        resources: ["Disease information", "Prevention guides"],
        assessment: "Research report"
      },
      {
        week: 8,
        topic: "Research Project Work",
        strand: "All",
        learningOutcomes: ["Develop research project", "Collect data"],
        activities: ["Project work", "Data collection"],
        resources: ["Project guidelines", "Research materials"],
        assessment: "Progress check"
      },
      {
        week: 9,
        topic: "Research Project Completion",
        strand: "All",
        learningOutcomes: ["Complete research project", "Write report"],
        activities: ["Report writing", "Final editing"],
        resources: ["Report templates", "Writing guides"],
        assessment: "Project submission"
      },
      {
        week: 10,
        topic: "Year 12 Review",
        strand: "All",
        learningOutcomes: ["Review all content", "Prepare for examination"],
        activities: ["Review sessions", "Practice tests"],
        resources: ["Review materials", "Past papers"],
        assessment: "End of year examination"
      }
    ] }
  },
  year13: {
    term1: { weeks: [
      {
        week: 1,
        topic: "Introduction to Development Theories",
        strand: "DST1",
        learningOutcomes: ["Understand theory purposes", "Preview major theories"],
        activities: ["Theory overview", "Discussion"],
        resources: ["Theory summaries", "Reading materials"],
        assessment: "Concept quiz"
      },
      {
        week: 2,
        topic: "Modernization Theory",
        strand: "DST1",
        learningOutcomes: ["Explain Rostow's stages", "Analyze assumptions"],
        activities: ["Stage analysis", "Critical discussion"],
        resources: ["Rostow's model", "Country examples"],
        assessment: "Analysis exercise"
      },
      {
        week: 3,
        topic: "Critiques of Modernization",
        strand: "DST1",
        learningOutcomes: ["Evaluate theory limitations", "Consider alternatives"],
        activities: ["Critical analysis", "Debate"],
        resources: ["Critique articles", "Alternative examples"],
        assessment: "Critical essay"
      },
      {
        week: 4,
        topic: "Dependency Theory",
        strand: "DST1",
        learningOutcomes: ["Explain core-periphery model", "Analyze exploitation"],
        activities: ["Model analysis", "Case studies"],
        resources: ["Dependency diagrams", "Historical examples"],
        assessment: "Model application"
      },
      {
        week: 5,
        topic: "Comparing Development Theories",
        strand: "DST1",
        learningOutcomes: ["Compare theories", "Apply to Pacific"],
        activities: ["Comparison chart", "Pacific analysis"],
        resources: ["Theory summaries", "Pacific data"],
        assessment: "Comparison essay"
      },
      {
        week: 6,
        topic: "Sustainable Development",
        strand: "DST1",
        learningOutcomes: ["Define sustainable development", "Explain three pillars"],
        activities: ["Pillar analysis", "Case studies"],
        resources: ["SD materials", "UN documents"],
        assessment: "Concept quiz"
      },
      {
        week: 7,
        topic: "Alternative Development Approaches",
        strand: "DST1",
        learningOutcomes: ["Explore alternative paths", "Discuss indigenous approaches"],
        activities: ["Alternative research", "Discussion"],
        resources: ["Alternative examples", "Indigenous development materials"],
        assessment: "Research presentation"
      },
      {
        week: 8,
        topic: "Globalization Introduction",
        strand: "DST2",
        learningOutcomes: ["Define globalization", "Identify drivers"],
        activities: ["Globalization mapping", "Discussion"],
        resources: ["Globalization materials", "Examples"],
        assessment: "Concept quiz"
      },
      {
        week: 9,
        topic: "Economic Globalization",
        strand: "DST2",
        learningOutcomes: ["Analyze economic integration", "Discuss impacts"],
        activities: ["Trade analysis", "Impact assessment"],
        resources: ["Trade data", "Economic reports"],
        assessment: "Impact analysis"
      },
      {
        week: 10,
        topic: "Cultural Globalization",
        strand: "DST2",
        learningOutcomes: ["Explain cultural spread", "Discuss effects on Vanuatu"],
        activities: ["Cultural analysis", "Local examples"],
        resources: ["Cultural examples", "Local observations"],
        assessment: "Reflection essay"
      }
    ] },
    term2: { weeks: [
      {
        week: 1,
        topic: "Winners and Losers of Globalization",
        strand: "DST2",
        learningOutcomes: ["Analyze globalization impacts", "Identify winners/losers"],
        activities: ["Impact analysis", "Debate"],
        resources: ["Impact studies", "Debate materials"],
        assessment: "Debate participation"
      },
      {
        week: 2,
        topic: "World Trade Organizations",
        strand: "DST2",
        learningOutcomes: ["Identify WTO, IMF, World Bank", "Explain their roles"],
        activities: ["Organization research", "Analysis"],
        resources: ["Organization profiles", "Policy examples"],
        assessment: "Research report"
      },
      {
        week: 3,
        topic: "Trade Agreements and Pacific",
        strand: "DST2",
        learningOutcomes: ["Analyze trade agreements", "Discuss Pacific involvement"],
        activities: ["Agreement analysis", "Pacific focus"],
        resources: ["Trade agreement texts", "Pacific trade data"],
        assessment: "Analysis exercise"
      },
      {
        week: 4,
        topic: "Development Planning",
        strand: "DST3",
        learningOutcomes: ["Understand planning process", "Learn planning tools"],
        activities: ["Planning workshop", "Tool practice"],
        resources: ["Planning guides", "Templates"],
        assessment: "Planning exercise"
      },
      {
        week: 5,
        topic: "Community Development Planning",
        strand: "DST3",
        learningOutcomes: ["Design local development plan", "Apply planning tools"],
        activities: ["Plan development", "Community consultation"],
        resources: ["Planning templates", "Consultation guides"],
        assessment: "Draft plan"
      },
      {
        week: 6,
        topic: "Development Plan Presentation",
        strand: "DST3",
        learningOutcomes: ["Present development plans", "Receive feedback"],
        activities: ["Plan presentations", "Peer evaluation"],
        resources: ["Presentation materials", "Evaluation forms"],
        assessment: "Plan presentation"
      },
      {
        week: 7,
        topic: "Environmental Security",
        strand: "DST4",
        learningOutcomes: ["Define environmental security", "Analyze threats"],
        activities: ["Threat analysis", "Discussion"],
        resources: ["Security materials", "Environmental reports"],
        assessment: "Analysis quiz"
      },
      {
        week: 8,
        topic: "Natural Disasters and Development",
        strand: "DST4",
        learningOutcomes: ["Identify disaster types", "Discuss risk reduction"],
        activities: ["Disaster case studies", "DRR planning"],
        resources: ["Disaster data", "DRR materials"],
        assessment: "Case study analysis"
      },
      {
        week: 9,
        topic: "Disaster Management in Vanuatu",
        strand: "DST4",
        learningOutcomes: ["Explain NDMO role", "Analyze Vanuatu preparedness"],
        activities: ["NDMO research", "Preparedness assessment"],
        resources: ["NDMO materials", "Disaster plans"],
        assessment: "Assessment report"
      },
      {
        week: 10,
        topic: "Mid-Year Review",
        strand: "All",
        learningOutcomes: ["Review Term 1-2 content", "Prepare for trials"],
        activities: ["Review sessions", "Practice tests"],
        resources: ["Review materials", "Past papers"],
        assessment: "Trial examination"
      }
    ] },
    term3: { weeks: [
      {
        week: 1,
        topic: "Political Development",
        strand: "DST5",
        learningOutcomes: ["Define democracy", "Explain governance principles"],
        activities: ["Governance analysis", "Discussion"],
        resources: ["Governance materials", "Examples"],
        assessment: "Concept quiz"
      },
      {
        week: 2,
        topic: "Vanuatu's Political System",
        strand: "DST5",
        learningOutcomes: ["Describe government structure", "Analyze challenges"],
        activities: ["System analysis", "Discussion"],
        resources: ["Constitution", "Government guides"],
        assessment: "System analysis"
      },
      {
        week: 3,
        topic: "Good Governance",
        strand: "DST5",
        learningOutcomes: ["Explain good governance principles", "Evaluate Vanuatu"],
        activities: ["Principle analysis", "Evaluation exercise"],
        resources: ["Governance indicators", "Evaluation tools"],
        assessment: "Evaluation report"
      },
      {
        week: 4,
        topic: "Corruption and Development",
        strand: "DST5",
        learningOutcomes: ["Analyze corruption impacts", "Discuss solutions"],
        activities: ["Impact analysis", "Solution brainstorming"],
        resources: ["Corruption data", "Anti-corruption materials"],
        assessment: "Analysis essay"
      },
      {
        week: 5,
        topic: "Human Rights",
        strand: "DST5",
        learningOutcomes: ["Explain UDHR", "Identify human rights issues"],
        activities: ["Rights analysis", "Case studies"],
        resources: ["UDHR text", "Rights reports"],
        assessment: "Case study analysis"
      },
      {
        week: 6,
        topic: "Gender and Development",
        strand: "DST5",
        learningOutcomes: ["Analyze gender issues", "Discuss equality"],
        activities: ["Gender analysis", "Discussion"],
        resources: ["Gender data", "Equality materials"],
        assessment: "Analysis exercise"
      },
      {
        week: 7,
        topic: "Education and Development",
        strand: "DST5",
        learningOutcomes: ["Explain education-development link", "Analyze challenges"],
        activities: ["Education data analysis", "Discussion"],
        resources: ["Education statistics", "Policy documents"],
        assessment: "Data analysis"
      },
      {
        week: 8,
        topic: "VSSC Examination Preparation",
        strand: "All",
        learningOutcomes: ["Review all content", "Practice exam techniques"],
        activities: ["Intensive review", "Practice papers"],
        resources: ["Review materials", "Past papers"],
        assessment: "Practice tests"
      },
      {
        week: 9,
        topic: "Final Review",
        strand: "All",
        learningOutcomes: ["Complete final review", "Address weak areas"],
        activities: ["Targeted review", "Q&A sessions"],
        resources: ["Review notes", "Question banks"],
        assessment: "Final preparation"
      },
      {
        week: 10,
        topic: "VSSC Examination",
        strand: "All",
        learningOutcomes: ["Complete external examination"],
        activities: ["Examination"],
        resources: ["Examination materials"],
        assessment: "VSSC Examination"
      }
    ] }
  }
};

export default developmentStudiesWeeklyLessons;
